import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { StoreNames } from '../../../shared/store-names';
import { useInventoryApi } from '../services/api';
import { InventoryReplenishmentStatusSummary } from '../interfaces';

export const useInventoryAvoidReplenishmentStatusStore = defineStore(
  StoreNames.InventoryAvoidReplenishmentStatus,
  () => {
    const status = ref<InventoryReplenishmentStatusSummary>({
      skus: {
        active: 0,
        inactive: 0,
      },
      locations: {
        active: 0,
        inactive: 0,
      },
      inventories: {
        active: 0,
        inactive: 0,
      },
    });

    const skus = computed(() => status.value.skus);
    const locations = computed(() => status.value.locations);
    const inventories = computed(() => status.value.inventories);

    const { getLocationsSummary, getSkusSummary, getInventoriesSummary } = useInventoryApi();

    const fetching = ref(false);
    const fetched = ref(false);

    let fetchingPromise: Promise<void> | null = null;

    async function fetch(force = false) {
      if (fetched.value && !force) {
        return fetchingPromise ?? Promise.resolve();
      }

      if (!fetchingPromise) {
        fetchingPromise = (async () => {
          fetching.value = true;

          const [locationsResponse, skusResponse, inventoriesResponse] = await Promise.all([
            getLocationsSummary(),
            getSkusSummary(),
            getInventoriesSummary(),
          ]);

          status.value = {
            locations: {
              active: locationsResponse.data.active,
              inactive: locationsResponse.data.inactive,
            },
            skus: {
              active: skusResponse.data.active,
              inactive: skusResponse.data.inactive,
            },
            inventories: {
              active: inventoriesResponse.data.active,
              inactive: inventoriesResponse.data.inactive,
            },
          };

          fetching.value = false;
          fetched.value = true;
          fetchingPromise = null;
        })();
      }

      return fetchingPromise;
    }

    return {
      fetching,
      fetched,
      fetch,
      skus,
      locations,
      inventories,
    };
  },
);
